import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import {
  AdminPatrolSystemMapping,
  UpsertAdminPatrolSystemMappingBody,
} from "../../../../../http/patrol/types/patrolSystemMappings";
import { AdminRoofCategory } from "../../../../../http/patrol/types/roofCategories";
import { AdminRoofMaterial } from "../../../../../http/patrol/types/roofMaterials";
import { AdminRoofSubstructure, RoofSubstructureSize } from "../../../../../http/patrol/types/roofSubstructures";
import { usePatrolSystemMappingsApi } from "../../../../../http/patrol/usePatrolSystemMappings";
import { useRoofCategoriesApi } from "../../../../../http/patrol/useRoofCategories";
import { useRoofMaterialsApi } from "../../../../../http/patrol/useRoofMaterials";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";
import styles from "./SystemMappingForm.module.css";

interface SystemMappingFormBody {
  category: AdminRoofCategory | null;
  roofMaterial: AdminRoofMaterial | null;
  roofSubstructure: AdminRoofSubstructure | null;
  minSubstructureHeight: number | null;
  minSubstructureSize: Partial<RoofSubstructureSize> | null;
  minMetalPlateThickness: number | null;
  roofWeightRequired: boolean;
  seamsSpanRequired: boolean;
}

interface SystemMappingDetailsProps {
  systemMapping?: AdminPatrolSystemMapping;
  disabled?: boolean;
  onClose: () => void;
}

const SystemMappingForm: FC<SystemMappingDetailsProps> = ({
  systemMapping,
  disabled,
  onClose,
}: SystemMappingDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aborter = useRef(new AbortController());

  const categoriesApi = useRoofCategoriesApi();
  const roofMaterialApi = useRoofMaterialsApi();
  const roofSubstructureApi = useRoofSubstructuresApi();
  const systemMappingsApi = usePatrolSystemMappingsApi();

  const categoriesResult = categoriesApi.useAdminRoofCategories({}, aborter.current.signal);
  const roofMaterialResult = roofMaterialApi.useAdminRoofMaterials({}, aborter.current.signal);
  const roofSubstructureResult = roofSubstructureApi.useAdminRoofSubstructures({}, aborter.current.signal);

  const defaultValues: SystemMappingFormBody = {
    category: systemMapping?.roofCategory ?? null,
    roofMaterial: systemMapping?.roofMaterial ?? null,
    roofSubstructure: systemMapping?.roofSubstructure ?? null,
    minSubstructureHeight: systemMapping?.minSubstructureHeight ?? null,
    minSubstructureSize: systemMapping?.minSubstructureSize ?? null,
    minMetalPlateThickness: systemMapping?.minMetalPlateThickness ?? null,
    roofWeightRequired: systemMapping?.roofWeightRequired ?? false,
    seamsSpanRequired: systemMapping?.seamsSpanRequired ?? false,
  };

  const onSubmit = () => async (formData: SystemMappingFormBody) => {
    setIsSubmitting(true);

    const body: UpsertAdminPatrolSystemMappingBody = {
      systemId: patrolSystemId!,
      roofCategoryId: formData.category?.id!,
      roofMaterialId: formData.roofMaterial?.id!,
      roofSubstructureId: formData.roofSubstructure?.id,
      minSubstructureHeight: formData.minSubstructureHeight!,
      minSubstructureSize:
        formData.minSubstructureSize?.height && formData.minSubstructureSize?.width
          ? {
              height: formData.minSubstructureSize.height!,
              width: formData.minSubstructureSize.width!,
            }
          : undefined,
      minMetalPlateThickness: formData.minMetalPlateThickness!,
      roofWeightRequired: formData.roofWeightRequired,
      seamsSpanRequired: formData?.seamsSpanRequired ?? false,
    };
    if (systemMapping?.id) {
      try {
        await systemMappingsApi.updateAdminPatrolSystemMapping(systemMapping.id, body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await systemMappingsApi.insertAdminPatrolSystemMapping(body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SystemMappingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, setValue, watch, formState: { errors } }) => {
        const roofWeightRequired = !!watch("roofWeightRequired");
        const seamsSpanRequired = !!watch("seamsSpanRequired");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofCategories.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofCategory>
                      {...field}
                      inputId={labelId}
                      options={categoriesResult.data?.items ?? []}
                      isDisabled={!!categoriesResult.error || isSubmitting}
                      isLoading={categoriesResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="roofMaterial"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofMaterials.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofMaterial>
                      {...field}
                      inputId={labelId}
                      options={roofMaterialResult.data?.items ?? []}
                      isDisabled={!!roofMaterialResult.error || isSubmitting}
                      isLoading={roofMaterialResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="roofSubstructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofSubstructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofSubstructure>
                      {...field}
                      inputId={labelId}
                      options={roofSubstructureResult.data?.items ?? []}
                      isDisabled={!!roofSubstructureResult.error || isSubmitting}
                      isLoading={roofSubstructureResult.isLoading}
                      isClearable
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minSubstructureHeight"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && (watch("minSubstructureSize.height") || watch("minMetalPlateThickness"))) {
                    return i18n.translation.common.youCannotMixUpSubstructureHeightSizeAndThickness;
                  }
                  return undefined;
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minSubstructureHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("minSubstructureHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField
              label={i18n.translation.common.minSubstructureSize}
              error={errors.minSubstructureSize?.height || errors.minSubstructureSize?.width}
              isOptional
            >
              {({ labelId }) => (
                <div className={styles.substructureSize}>
                  <span>{i18n.translation.common.height}:</span>
                  <Controller
                    name="minSubstructureSize.height"
                    rules={{
                      validate: (value) => {
                        if (value && (watch("minSubstructureHeight") || watch("minMetalPlateThickness"))) {
                          return i18n.translation.common.youCannotMixUpSubstructureHeightSizeAndThickness;
                        }
                        if (
                          !value &&
                          watch("minSubstructureSize.width") &&
                          !watch("minSubstructureHeight") &&
                          !watch("minMetalPlateThickness")
                        ) {
                          return i18n.translation.common.pleaseSpecifyAHeight;
                        }
                        return;
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId + "_height"}
                        disabled={disabled || isSubmitting}
                        isInvalid={!!errors.minSubstructureSize?.height}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("minSubstructureSize.height", value ?? undefined)}
                      />
                    )}
                  />
                  <span>{i18n.translation.common.width}:</span>
                  <Controller
                    name="minSubstructureSize.width"
                    rules={{
                      validate: (value) => {
                        if (value && (watch("minSubstructureHeight") || watch("minMetalPlateThickness"))) {
                          return i18n.translation.common.youCannotMixUpSubstructureHeightSizeAndThickness;
                        }
                        if (
                          !value &&
                          watch("minSubstructureSize.height") &&
                          !watch("minSubstructureHeight") &&
                          !watch("minMetalPlateThickness")
                        ) {
                          return i18n.translation.common.pleaseSpecifyAWidth;
                        }
                        return;
                      },
                    }}
                    control={control}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId + "_wminSubstructureSize.width"}
                        disabled={disabled || isSubmitting}
                        isInvalid={!!errors.minSubstructureSize?.width}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("minSubstructureSize.width", value ?? undefined)}
                      />
                    )}
                  />
                </div>
              )}
            </FormField>
            <Controller
              name="minMetalPlateThickness"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && (watch("minSubstructureSize") || watch("minSubstructureHeight"))) {
                    return i18n.translation.common.youCannotMixUpSubstructureHeightSizeAndThickness;
                  }
                  return undefined;
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minMetalPlateThickness} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("minMetalPlateThickness", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.roofWeightRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={roofWeightRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("roofWeightRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.seamsSpanRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={seamsSpanRequired}
                  disabled={disabled}
                  onChange={(checked) => setValue("seamsSpanRequired", checked)}
                />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemMappingForm;
